import { Table, TablePaginationConfig } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DownloadOutlined } from '@ant-design/icons';
import api, { Payment, SortOrder, StatementStatus, StatementsSortField, StripeBalanceDto } from 'api';
import { getCellValue, getShowTotal } from 'utils/tableUtils';
import moment from 'moment';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import { TStatementStatus } from 'utils/productUtils';

type ParamsProps = {
	projectId: string;
};
const getColumns = (t: TFunction) => [
	{
		title: t('financePage.PaymentsTab.Month'),
		key: 'month',
		render: (record: StripeBalanceDto) => getCellValue(moment(record.month).format('MMM YYYY')),
	},
	{
		title: t('financePage.PaymentsTab.Amount'),
		key: 'amount',
		render: (record: StripeBalanceDto) =>
			getCellValue(
				Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: record.currency || 'usd',
				}).format(record.amount || 0),
			),
	},
	{
		title: t('financePage.PaymentsTab.Status'),
		key: 'status',
		render: (record: StripeBalanceDto) =>
			getCellValue(t(TStatementStatus[record.status !== undefined ? record.status : StatementStatus.Accrued])),
	},
	{
		title: t('financePage.PaymentsTab.Download'),
		key: 'download',
		render: (record: StripeBalanceDto) =>
			record.statementUrl && (
				<DownloadOutlined
					data-testid="statement-download-button"
					onClick={() => {
						downloadFileByObjectURL(record.statementUrl || '', 'statement.pdf');
					}}
				/>
			),
	},
];
export default function Payments() {
	const { t } = useTranslation();
	const { projectId } = useParams<ParamsProps>();
	const [paymentDetails, setPaymentDetails] = useState<Payment[]>();
	const columns = useMemo(() => getColumns(t), [t]);
	const [isLoading, setIsLoading] = useState(false);
	const showTotal = useCallback((total: number, range: [number, number]) => getShowTotal(total, range, t), [t]);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});

	const handleTableChange = async (tablePagination: TablePaginationConfig) => {
		setIsLoading(true);

		api
			.getProjectStatements(
				(tablePagination.current as number) - 1,
				tablePagination.pageSize as number,
				StatementsSortField.Month,
				SortOrder.DESC,
				parseInt(projectId),
			)
			.then((result) => {
				setIsLoading(false);
				setPaymentDetails(result.items || []);
				setPagination({
					...pagination,
					...tablePagination,
					total: result.totalCount || 0,
				});
			});
	};
	useEffect(() => {
		handleTableChange(pagination);
	}, []);

	return (
		<>
			<Table
				className="table"
				size="middle"
				columns={columns}
				rowKey={(record) => record.id || 0}
				dataSource={paymentDetails}
				pagination={pagination}
				loading={isLoading}
				onChange={handleTableChange}
				tableLayout="fixed"
				data-testid="paymentDetailsTable"
			/>
		</>
	);
}
